import { Divider, Grid } from '@mui/material';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ClientDTO } from 'wisestep-api';
import {
  SearchBarNew,
  WsAutoComplete,
  WsBadge,
  WsBadgeSize,
  WsButton,
  WsColors,
  WsColorsNew,
  WsBadgetype,
} from 'wisestep-base';

import { ClientAutocomplete } from '../../../../components/ClientAutocomplete';
import {
  IFilters,
  contactsDefaultFilterValues,
} from '../../../../features/clientContacts/helper/ContactListingHelper';
import { StyledBox } from '../../../../features/clientListingPage/helper/ClientListingHelper';
import { getFilterValue } from '../../../../features/notes/helper/NotesHelper';
import SmartFilter from '../../../../features/submissionsNew/components/filters/smart/SmartFilter';

export interface IContactFilters {
  searchQuery: IFilters;
  clear: () => void;
  getUpdatedFiltersFromFilters: (data) => void;
  tags: string[];
  contactsCount?: number;
  onAddButtonClick?: () => void;
  isClientContactTab?: boolean;
}

const ContactFilters = ({
  searchQuery,
  clear,
  getUpdatedFiltersFromFilters,
  tags,
  isClientContactTab = false,
}: IContactFilters) => {
  const methods = useForm<IFilters>({
    defaultValues: searchQuery ?? contactsDefaultFilterValues,
  });
  const { handleSubmit, getValues, setValue, reset, watch } = methods;
  const [status, setStatus] = useState<string[]>(searchQuery.status ?? []);
  const [contactTags, setContactTags] = useState<string[]>();
  const clientContactStatusFilterWatch = watch('status', []);
  const clientFilterWatch = watch('clients', []);
  const tagsFilterWatch = watch('tags', []);

  const onSearch = () => handleFilters();

  const handleClear = () => {
    setContactTags([]);
    setStatus([]);
    reset(contactsDefaultFilterValues);
    clear();
  };

  const handleFilters = useCallback(async () => {
    const updatedQuery = {
      ...searchQuery,
      search: getValues('search') ?? '',
      status: getValues('status') ?? [],
      tags: getValues('tags') ?? [],
      clients: getValues('clients') ?? [],
    };
    getUpdatedFiltersFromFilters(updatedQuery);
  }, [searchQuery, getUpdatedFiltersFromFilters, getValues]);

  const onStatusSelection = async (statusInput: string[]) => {
    setStatus(statusInput);
    setValue('status', statusInput);
  };

  const onTagsSelection = async (tagsInput: string[]) => {
    setContactTags(tagsInput);
    setValue('tags', tagsInput);
  };

  const handleStatusClear = (label: string) => {
    const values = getValues();
    const updatedStatus: string[] | undefined =
      values.status && Array.isArray(values.status)
        ? values.status.filter((item) => item !== label)
        : undefined;
    setValue('status', updatedStatus ?? []);
    onSearch();
  };
  const handleClientClear = (label: string) => {
    const values = getValues();
    const updatedStatus: ClientDTO[] | undefined =
      values.clients && Array.isArray(values.clients)
        ? values.clients.filter((item) => item.id !== label)
        : undefined;
    setValue('clients', updatedStatus ?? []);
    onSearch();
  };

  const handleTagClear = (label: string) => {
    const values = getValues();
    const updatedStatus: string[] | undefined =
      values.tags && Array.isArray(values.tags)
        ? values.tags.filter((item) => item !== label)
        : undefined;
    setValue('tags', updatedStatus);
    onSearch();
  };

  return (
    <StyledBox sx={{ border: 'none' }}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSearch)}>
          <Grid container bgcolor={WsColors.White} sx={{ ml: 2 }}>
            <Grid item xs={4}>
              <SearchBarNew
                size="small"
                name="search"
                placeholder="Search Contacts"
                type="submit"
                onClear={handleClear}
              />
            </Grid>
            <Grid item>
              <SmartFilter
                filterName="Contact Status "
                onClick={onSearch}
                filterValue={getFilterValue('status', methods)}
                onClear={() => {}}
              >
                <WsAutoComplete
                  onTagsChange={onStatusSelection}
                  placeholder="Eg, Michael"
                  initialValue={status}
                  optionsInStringArray={['All', 'Active', 'Inactive']}
                  value={status}
                  multiple={true}
                  freeSolo={true}
                  name="status"
                  size="small"
                />
              </SmartFilter>
            </Grid>
            {isClientContactTab && (
              <Grid item>
                <SmartFilter
                  filterName="Company "
                  onClick={onSearch}
                  filterValue={getFilterValue('clients', methods)}
                  onClear={() => {}}
                >
                  <ClientAutocomplete name="clients" multiple />
                </SmartFilter>
              </Grid>
            )}
            <Grid item>
              <SmartFilter
                filterName="Contact Tags "
                onClick={onSearch}
                filterValue={getFilterValue('tags', methods)}
                onClear={() => {}}
              >
                <WsAutoComplete
                  onTagsChange={onTagsSelection}
                  placeholder="Eg, Michael"
                  initialValue={contactTags ?? []}
                  value={contactTags}
                  optionsInStringArray={tags}
                  multiple={true}
                  freeSolo={true}
                  name="tags"
                  size="small"
                />
              </SmartFilter>
            </Grid>
          </Grid>
          <Grid container pt={2} spacing={1} style={{ display: 'flex', alignItems: 'center' }}>
            {clientContactStatusFilterWatch &&
              clientContactStatusFilterWatch.map((label, index) => (
                <Grid item key={index}>
                  <WsBadge
                    label={`Status: ${label}`}
                    badgeType={WsBadgetype.INACTIVE}
                    endIcon={true}
                    onClick={() => handleStatusClear(label)}
                    menuList={false}
                    badgeSize={WsBadgeSize.LARGE}
                  />
                </Grid>
              ))}

            {clientFilterWatch &&
              clientFilterWatch.map((label, index) => (
                <Grid item key={index}>
                  <WsBadge
                    label={`Client: ${label.name}`}
                    badgeType={WsBadgetype.INACTIVE}
                    endIcon={true}
                    onClick={() => handleClientClear(label.id)}
                    menuList={false}
                    badgeSize={WsBadgeSize.LARGE}
                  />
                </Grid>
              ))}

            {tagsFilterWatch?.map((label, index) => (
              <Grid item key={index}>
                <WsBadge
                  label={`Tags: ${label}`}
                  badgeType={WsBadgetype.INACTIVE}
                  endIcon={true}
                  onClick={() => handleTagClear(label)}
                  menuList={false}
                  badgeSize={WsBadgeSize.LARGE}
                />
              </Grid>
            ))}

            {((clientContactStatusFilterWatch?.length ?? 0) > 0 ||
              clientFilterWatch?.[0]?.value ||
              (tagsFilterWatch?.length ?? 0) > 0) && (
              <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    backgroundColor: WsColorsNew.neutral_400a,
                    height: 30,
                    mx: 0.5,
                  }}
                />
                <Grid item pl={0.5}>
                  <WsButton onClick={handleClear} variant="text">
                    Clear Filters
                  </WsButton>
                </Grid>
              </Grid>
            )}
          </Grid>
        </form>
      </FormProvider>
    </StyledBox>
  );
};

export default ContactFilters;
